import createClient, { type Client, type ClientOptions, type Middleware } from "openapi-fetch";

export interface ServiceClientOptions extends Omit<ClientOptions, "baseUrl"> {
  /** e.g. `http://localhost:8002` or `https://shop.example/api/v1/store` (gateway prefix). */
  baseUrl: string;
  /** Returns the bearer token for the current principal (staff JWT, POS device, customer OTP). */
  getAccessToken?: () => string | null | undefined | Promise<string | null | undefined>;
  /** Locale sent as `Accept-Language` so error messages come back translated (design/07 §1). */
  getLocale?: () => string | undefined;
  /** Generates `X-Request-Id`; defaults to `crypto.randomUUID()`. */
  requestId?: () => string;
}

/** Idempotency-Key is mandatory on every POST that creates an order/payment/adjustment. */
export function idempotencyKey(): string {
  return crypto.randomUUID();
}

/**
 * Typed openapi-fetch client with the platform conventions applied on every request:
 * request-id, bearer auth, Accept-Language and JSON accept header. `Paths` comes from the
 * generated `schema.d.ts` of the target service.
 */
export function createServiceClient<Paths extends object>(
  service: string,
  options: ServiceClientOptions,
): Client<Paths> {
  const {
    getAccessToken,
    getLocale,
    requestId = () => crypto.randomUUID(),
    ...clientOptions
  } = options;
  const client = createClient<Paths>(clientOptions);

  const conventions: Middleware = {
    async onRequest({ request }) {
      if (!request.headers.has("X-Request-Id")) {
        request.headers.set("X-Request-Id", requestId());
      }
      request.headers.set("X-Service", service);
      if (!request.headers.has("Accept")) {
        request.headers.set("Accept", "application/json");
      }
      const locale = getLocale?.();
      if (locale && !request.headers.has("Accept-Language")) {
        request.headers.set("Accept-Language", locale);
      }
      const token = await getAccessToken?.();
      if (token && !request.headers.has("Authorization")) {
        request.headers.set("Authorization", `Bearer ${token}`);
      }
      return request;
    },
  };
  client.use(conventions);
  return client;
}
